import { z } from "zod";

import { NoteSchema } from "../note";
import { BrazilAddressSchema } from "./address";
import { cpfSchema } from "./br-tax-id";
import {
  clientMetadataFields,
  CLIENT_RECORD_GROUPS,
  ContractSchema,
  FleetEntrySchema,
  PaymentTermsSchema,
  ReachabilitySchema,
} from "./common";
import { LeadSource } from "./company";

const personCommonFields = {
  taxId: cpfSchema.optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
  notes: z.array(NoteSchema).default([]).meta({ visibleTo: CLIENT_RECORD_GROUPS }),
};

const isDobYearInRange = (v: string): boolean => {
  const year = Number(v.slice(0, 4));

  return year >= 1900 && year <= new Date().getFullYear();
};
const isDobInPast = (v: string): boolean =>
  new Date(`${v}T00:00:00`) < new Date(new Date().toDateString());

const dateOfBirthSchema = z.iso
  .date({ message: "clients_validation_dob_invalid_format" })
  .refine(isDobYearInRange, { message: "clients_validation_dob_invalid_format" })
  .refine(isDobInPast, { message: "clients_validation_dob_future" });

export const PersonClientFieldsSchema = z
  .object({
    type: z.literal("person").meta({ visibleTo: "*" }),
    name: z
      .string({ message: "clients_validation_name_required" })
      .min(1, { message: "clients_validation_name_required" })
      .max(120)
      .meta({ visibleTo: "*" }),
    dateOfBirth: dateOfBirthSchema.optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    address: BrazilAddressSchema.optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    profession: z.string().max(100).optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    referral: z.string().max(120).optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    contact: ReachabilitySchema.optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    salesConsultantId: z.string().max(100).optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    leadSource: LeadSource.optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    fleet: z.array(FleetEntrySchema).default([]).meta({
      visibleTo: CLIENT_RECORD_GROUPS,
    }),
    contract: ContractSchema.optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    paymentTerms: PaymentTermsSchema.optional().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
    usesExternalCaseIdentifier: z
      .boolean()
      .default(false)
      .meta({ visibleTo: CLIENT_RECORD_GROUPS }),
  })
  .describe("Person client (CPF) profile fields.");

export type PersonClientFields = z.infer<typeof PersonClientFieldsSchema>;

export const CreatePersonClientSchema = PersonClientFieldsSchema.extend({
  taxId: cpfSchema.optional(),
  notes: z.string().max(4000).optional(),
}).describe("Payload to create a person client.");

export const PersonClientSchema = PersonClientFieldsSchema.extend(personCommonFields)
  .extend(clientMetadataFields)
  .describe("Persisted person client document.");
